import * as geo from 'd3-geo'
import * as d3 from 'd3-selection'
import { interpolate } from 'd3-interpolate'
import * as time from 'd3-timer'
import * as ease from 'd3-ease'

const circle = geo.geoCircle()

const epsilon = 1e-6

/*
 * Builds object of lat/long coords for both a globe and a flat map.
 */
const coords = () => {
  const coords = circle().coordinates[0] // This gives us 61 points
      , left = coords.slice(0, Math.ceil(coords.length / 2)) // Divide the in half to grab the left side of the globe points.
      , globe = [...left, ...left.reverse().map(coord => [coord[0] * -1, coord[1]])] // Use the lat of each left side and clone it to the right side
      , map = globe.map(coord => [Math.sign(coord[0]) * 180, coord[1]]) // The full map uses a long range of [-180, 180]

  return {globe, map}
}

/*
 *
 * Decorates the globe features. i.e. the graticule and globe outline
 * The default transform translates from a map to a globe.
 */
class Decorator {

  constructor ({ee, chart, path, projection, globeProjection, container, transformer = this.reproject}) {
    this.container = container
    this.chart = chart
    this.projection = projection
    this.globeProjection = globeProjection
    this.transformer = transformer

    let {globe, map} = coords()
    this.interpolater = interpolate(map, globe)
    this.opacity = interpolate(epsilon, 1)

    this.container.selectAll('path.globe-outline').data([{globe, map}]) // Bind the data
  }

  startRotation () {
    if (!this.container.classed('globe')) {
      // Can't spin if it's not a globe
      return
    }

    let projection = this.globeProjection
      , path = geo.geoPath()

    this.tiltTransition = this.container
                              .selectAll('path.graticule, path.feature')
                              .transition()
                              .duration(3000)
                              .ease(ease.easeLinear)

    this.tiltTransition
        .tween('tilt', function () {
          let interpolator = interpolate(0, -16.5)
          return t => {
            let rotation = projection.rotate()
            rotation[1] = interpolator(t)
            projection.rotate(rotation)
            d3.select(this).attr('d', path.projection(projection))
          }
        })

    let rotation = this.projection.rotate()
      , degPerMs = 6 / 1000

    const rotate = elapsed => {
      let now = time.now()
        , diff = now - this.lastTime

      if (diff < elapsed) {
        let rotation = projection.rotate()
        rotation[0] += diff * degPerMs
        projection.rotate(rotation)

        // Update the paths
        this.container
            .selectAll('path.graticule, path.feature')
            .attr('d', path.projection(projection))
      }

      this.lastTime = now
    }

    if (!this.chart.node().__autoRotater) {
      this.chart.node().__autoRotater = time.timer(rotate)
      this.lastTime = time.now()
    }

    this.chart.node().__autoRotater.restart(rotate, 0)
  }

  stopRotation () {
   this.container
       .selectAll('path.graticule, path.feature')
       .transition()

    if (this.chart.node().__autoRotater) {
      this.chart.node().__autoRotater.stop()
    }
  }

  reproject (t) {
    let points = this.interpolater(t)
      , projection = this.projection
      , bounds = [ projection([-180, 0])[0], projection([180, 0])[0] ]

    this.container
        .selectAll('path.graticule')
        .attr('d', geo.geoPath().projection(projection))

    this.container
      .selectAll('path.globe-outline')
      .attr('d', () => {
        // Manually draw the path by joining all the points clockwise.
        return points.reduce((p, coord) => {
          let left = Math.sign(coord[0]) < 0
          coord = projection(coord)

          // Make sure this point is bounded by projection
          if (left) {
            // left of the meridian
            coord[0] = Math.min(bounds[0], coord[0])
          } else {
            // right of the meridian
            coord[0] = Math.max(bounds[1], coord[0])
          }

          if (!p) {
            // move to start position
            p = `M${coord[0]},${coord[1]}`
          } else {
            // line to new point
            p += `L${coord[0]},${coord[1]}`
          }
          return p
        }, null) + 'Z'
      })
  }

  /*
   * Updates the globe outline using the outline interpolater
   * at the given time, `t`. To reverse the decorations, reverse
   * the `t` value.
   */
  transform (t) {
    this.transformer(t)

    this.container
        .selectAll('.globe-outline, .graticule')
        .style('opacity', this.opacity(t))
  }

}

export default Decorator
